/**
 * @bolao/i18n — translation resources, the typed key contract, and the i18next
 * configuration.
 *
 * Design rules this package exists to enforce (PLAN.md rules 1 and 2):
 *
 *   1. No user-visible string ever appears in code. Lint enforces it; this
 *      package is where the strings actually live.
 *   2. UI chrome and *content* are separated. Team names, stadiums and stage
 *      names are DATABASE content with per-locale translations keyed by stable
 *      IDs — they must never be added to these JSON files. Mixing the two is
 *      exactly what forced the legacy English fork.
 *
 * `en` is the source locale: every key must exist there, and `TranslationKeys`
 * is derived from it so a missing or misspelled key is a type error.
 *
 * The configuration lives here rather than in the web app so it can be unit
 * tested — a misconfigured locale chain fails silently by rendering the fallback
 * language, which is indistinguishable from "translation not written yet".
 */
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale } from '@bolao/shared';
import type { InitOptions } from 'i18next';
import en from '../locales/en/common.json' with { type: 'json' };
import ptBR from '../locales/pt-BR/common.json' with { type: 'json' };

export const NAMESPACE = 'common';

export const resources = {
  en: { [NAMESPACE]: en },
  'pt-BR': { [NAMESPACE]: ptBR },
} as const satisfies Record<Locale, Record<string, unknown>>;

/** Dot-separated leaf paths of the source locale, e.g. `"app.name"`. */
type Leaves<T> = T extends object
  ? { [K in keyof T & string]: T[K] extends object ? `${K}.${Leaves<T[K]>}` : K }[keyof T & string]
  : never;

export type TranslationKeys = Leaves<typeof en>;

/**
 * Shared i18next options.
 *
 * Note on `fallbackLng`: it is a map, not a string, so that a browser reporting
 * a Portuguese variant we do not ship (`pt`, `pt-PT`) lands on `pt-BR` instead of
 * English.
 *
 * Do NOT add `nonExplicitSupportedLngs: true` here. It strips the region from the
 * code being checked, so it asks whether `'pt'` is in `supportedLngs` — which it
 * is not, because we ship `'pt-BR'`. The effect is that `pt-BR` is treated as
 * unsupported and silently drops out of the resolution chain, rendering English
 * while `i18n.language` still reports `'pt-BR'`. The fallback map below is the
 * correct mechanism for the same goal.
 */
export function createI18nOptions(): InitOptions {
  return {
    resources,
    supportedLngs: [...SUPPORTED_LOCALES],
    fallbackLng: {
      pt: ['pt-BR'],
      default: [DEFAULT_LOCALE],
    },
    defaultNS: NAMESPACE,
    ns: [NAMESPACE],
    load: 'currentOnly',
    interpolation: {
      // React escapes for us; double-escaping mangles apostrophes.
      escapeValue: false,
    },
  };
}

export { DEFAULT_LOCALE, SUPPORTED_LOCALES };
export type { Locale };
